import { merge } from 'lodash';
import { PayDashboardTabs } from '@melio/ap-activities';
import {
  MelioConfig,
  PartialMelioConfig,
  PaymentSchedulingPreference,
  TrustedPartners,
} from '@melio/platform-provider';

import { defaultLinks } from '@/links';

declare global {
  interface Window {
    dynamicEnvConfig?: PartialMelioConfig;
  }
}
export const _defaultConfig: MelioConfig = {
  analytics: { key: '', shouldPrintEvents: false, shouldTrackEvents: false },
  featureFlagProvider: { clientId: '', shouldLoad: false },
  production: false,
  settings: {
    registerToMelioGetPaid: 'https://app.meliopayments.com/meliome/register',
    icons: {
      fundingSource: {
        bank: 'bank',
      },
      deliveryMethod: {
        virtualCard: 'mastercard',
      },
    },
    assets: {},
    payment: {
      scheduling: {
        scheduleBy: PaymentSchedulingPreference.ByScheduleDate,
        hideScheduleDateIndication: false,
      },
      showCreatedByIndication: false,
      memoToVendor: {
        maxLength: 50,
        deliveryMethodBlackList: [],
      },
    },
    fundingSourcePolicy: {
      fundingSourceCard: {
        showCreatedBy: false,
      },
      'bank-account': {
        order: 0,
        fundingSourceGroup: 'bank-account',
        read: false,
        write: false,
      },
      credit: {
        order: 1,
        fundingSourceGroup: 'credit',
        read: false,
        write: false,
      },
      debit: {
        order: 2,
        fundingSourceGroup: 'debit',
        read: false,
        write: false,
      },
    },
    vendor: {
      collectedDetails: 'minimal',
      vendorSelect: {
        createVendor: {
          showVendorDetailsForm: false,
        },
      },
      createVendor: {
        hasSkipDeliveryMethodButton: true,
        canAddVendorWithTheSameName: false,
        shouldSwitchToExtendedFormOnApiErrors: false,
      },
    },
    isMultiColoredAvatarsEnabled: true,
    isEmbeddedExperience: false,
    isPaymentApprovalLimitEnabled: false,
    isLineItemsEnabled: false,
    newBillExperience: {
      isRecurringEnabled: true,
    },
    payDashboardBillsTabTourEnabled: false,
    newPayExperience: {
      isEnabled: true,
      tabsOrder: [
        PayDashboardTabs.Vendors,
        PayDashboardTabs.Bills,
        PayDashboardTabs.Approvals,
        PayDashboardTabs.Payments,
        PayDashboardTabs.Financing,
      ],
      firstTimeExperience: {
        assetType: 'image',
        imageUrl: 'https://platform-static.meliopayments.com/assets/default/illustrations/first-time-experience.png',
        mobileImageUrl:
          'https://platform-static.meliopayments.com/assets/default/illustrations/first-time-experience.png',
        videoLink: '',
      },
    },
    isUnilateralDeliveryMethodSupported: true,
    isPaymentTimelineEnabled: false,
    contractorLearnMoreLink: 'https://google.com',
    tax1099LearnMoreLink: 'https://google.com',
    virtualCardLearnMoreLink:
      'https://melio.zendesk.com/hc/en-us/articles/4416039832732-Why-has-my-vendor-s-delivery-method-changed-to-single-use-virtual-card',
    showDemoRequestIcon: true,
    contextualOnboarding: {
      buttons: [],
    },
    privacyPolicyUrl:
      'https://www.meliopayments.com/legal/privacy-policy?_ga=2.9364146.662432983.1653317311-444475439.1647269629',
    goodsReceivedAmountThreshold: 3000,
    requireInvoiceForBillsAmountThreshold: 10000,
    helpCenterUrl: 'https://melio.zendesk.com/hc/en-us',
    fileSizeLimit: 10 * 1024 * 1024, // 10MB
    fileAllowedFormats: ['pdf', 'png', 'jpg'],
    tokenProvider: 'tabapay',
    eoyCheck: {
      enabled: false,
      promoteFastCheck: false,
      link: 'https://melio.zendesk.com/hc/en-us/articles/6548872564508-USPS-potential-check-delivery-delays-during-the-holidays',
    },
    unilateralRequest: {
      announceFees: false,
    },
    trustedPartners: [TrustedPartners.Mastercard, TrustedPartners.Quickbooks, TrustedPartners.Visa],
    mccLearnMoreUrl:
      'https://melio.zendesk.com/hc/en-us/articles/4415821314076-A-change-in-how-you-pay-using-your-American-Express-card',
    supportEmail: 'webmaster@example.com',
    termsOfServiceUrl:
      'https://www.meliopayments.com/legal/terms-of-service?_ga=2.47262112.662432983.1653317311-444475439.1647269629',
    learnMoreAccountVerificationURL: 'https://melio.zendesk.com/hc/en-us/articles/7104668877084',
    partnerDisplayName: 'Melio',
    virtualCardExpiryPeriodInDays: 0,
    deliveryMethodTypeOrder: {
      'bank-account': 1,
      'paper-check': 2,
      'domestic-wire-account': 3,
      'virtual-card': 4,
      'virtual-account': 5,
      'international-account': 6,
    },
    deliveryMethodTypeOptionDisabledReasons: {
      'bank-account': ['vendorIsBank'],
      'paper-check': [],
      'domestic-wire-account': [],
      'virtual-account': ['vendorIsBank'],
      'international-account': ['cardFundingSourceNotSupported', 'vendorIsBank'], // TODO: maybe merge with fundingSourceNotSupported
      'virtual-card': [],
    },
    syncProgressIndicationEnabled: false,
    vex: {
      zdSupportFormUrl: 'https://melio.zendesk.com/hc/en-us/requests/new',
      persistentFastOptOutArticleUrl:
        'https://melio.zendesk.com/hc/en-us/articles/12413481862812?utm_source=&utm_medium=&utm_campaign=opt-out-fast-preference',
      unilateral: {
        isEmailLoginEnabled: false,
        shouldSuggestJoinMelio: false,
        isNewUnilateral: false,
        suvcInformationLink: 'https://meliopayments.com/guides/single-use-virtual-cards/',
      },
    },
    showCheckMarkIconForManagedVendor: true,
  },
  services: {
    googleMaps: {
      apiKey: 'AIzaSyCy2BbnUakgGy08r_gmL8Qlo0VikreL7WA',
    },
    userSnap: {
      spaceId: '39ab2947-23d9-4768-a96f-9eff5f508557',
      projectId: '786dcee3-f5f6-42da-93f7-15db525c1e1b',
    },
    tbt: {
      apiKey: 'key_GZFc6KNn6zbN6Y54aFZfpV',
      supportedCardProviders: {
        visa: true,
        mastercard: true,
        'american-express': true,
        'diners-club': true,
        discover: true,
        unknown: false,
      },
      fontsUrl: 'https://platform-static.meliopayments.com/fonts/fonts-config.css',
    },
    forethoughtApiKey: '74ba8f52-1c1e-4b60-8b1e-bc74bcdd4a0d',
    zendeskKey: '6cf823a4-5854-401a-8e1f-c92a3b5d0f49', // Sandbox V.2 -> Melio - Sandbox Widget
    zendeskWorkflow: 'Melio Routing',
    googleClientId: '619633578816-tb15fe2uqjetng2fhfq7kbeo83e69kpp.apps.googleusercontent.com',
    tabapay: {
      card: {
        url: 'https://sso.sandbox.tabapay.com:8443/SSOMelioCap1.html',
      },
    },
    smartystreets: {
      apiKey: '27409250903411363',
    },
    plaid: {
      env: 'sandbox',
      apiKey: '7f066139be81ee696f3a4c27a78d7e',
    },
  },
  server: {
    apiServer: process.env.API_SERVER || '',
  },
  tutorialVideo: 'https://www.youtube.com/embed/eqg9fpX3tMk',
  dataDog: {
    shouldInit: false,
    appId: '1cae8098-c814-4441-84f4-d83a35abb272',
    clientId: 'pub012d37ef8b7e4b5efb04e359f4abf56c',
    site: 'datadoghq.com',
    service: 'platform-app',
  },
  zendesk: {
    useNewWidgetApi: false,
    headerColor: '#BFBFBF',
    resultListsColor: '#BFBFBF',
    themeColor: '#BFBFBF',
    title: 'Payment Support',
    logo: '',
  },
  links: defaultLinks,
};

export const defaultConfig = merge(_defaultConfig, window.dynamicEnvConfig);
